import { useState, useEffect, useRef } from "react";
import { LucideAlertTriangle, LucideServerCrash } from "lucide-react";
import { Modal } from "@/components/modals/Modal";
import { Button } from "@/components/ui/button";

const ERROR_CONFIGS = {
  "api-error:429": {
    title: "Too many requests",
    body: "You've made too many requests in a short time. Please wait a moment and try again.",
    icon: <LucideAlertTriangle size={18} className="text-warning" />,
    iconBg: "bg-warning-bg",
  },
  "api-error:403": {
    title: "Access denied",
    body: "You don't have permission to perform this action.",
    icon: <LucideAlertTriangle size={18} className="text-warning" />,
    iconBg: "bg-warning-bg",
  },
  "api-error:500": {
    title: "Something went wrong",
    body: "We encountered a server error. Please try again in a moment. If the problem persists, contact support.",
    icon: <LucideServerCrash size={18} className="text-muted-foreground" />,
    iconBg: "bg-muted",
  },
};

const AUTO_CLOSE_MS = 8000;

/**
 * Global modal that listens for api-error:429, api-error:403, and api-error:500
 * custom window events and presents a contextual message to the user.
 *
 * Mount once inside RootLayout. Shows at most one modal at a time; subsequent
 * events are ignored until the current modal is closed.
 */
export function ApiErrorModal() {
  const [activeError, setActiveError] = useState(null); // one of the EVENT keys above
  const timerRef = useRef(null);

  function close() {
    setActiveError(null);
    clearTimeout(timerRef.current);
  }

  useEffect(() => {
    const eventKeys = Object.keys(ERROR_CONFIGS);

    function handleError(key) {
      // Only show one modal at a time - ignore if already showing
      setActiveError((current) => {
        if (current) return current;

        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(close, AUTO_CLOSE_MS);
        return key;
      });
    }

    const listeners = eventKeys.map((key) => {
      const handler = () => handleError(key);
      window.addEventListener(key, handler);
      return { key, handler };
    });

    return () => {
      listeners.forEach(({ key, handler }) => window.removeEventListener(key, handler));
      clearTimeout(timerRef.current);
    };
  }, []);

  const config = activeError ? ERROR_CONFIGS[activeError] : null;

  return (
    <Modal
      open={Boolean(activeError)}
      onClose={close}
      maxWidth="max-w-sm"
    >
      {config && (
        <div className="px-6 py-5 flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${config.iconBg}`}
            >
              {config.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight">
                {config.title}
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {config.body}
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={close}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
